import React, { useEffect, useState } from 'react';
import { Users, Smartphone, Tv, Zap } from 'lucide-react';
import { Card, StatCard } from '../components/UI';
import { analyticsAPI, configAPI } from '../api/apiClient';
import toast from 'react-hot-toast';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

/**
 * Dashboard Page Component
 */
const DashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [userGrowth, setUserGrowth] = useState([]);
  const [channelViews, setChannelViews] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [statsRes, growthRes, viewsRes, configRes] = await Promise.all([
        analyticsAPI.getDashboardStats(),
        analyticsAPI.getUserGrowth(30),
        analyticsAPI.getChannelViews(10),
        configAPI.get(),
      ]);

      setStats(statsRes.data.data);
      setUserGrowth(growthRes.data.data);
      setChannelViews(viewsRes.data.data);
      setConfig(configRes.data.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold text-white">Dashboard</h1>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="h-32 bg-gray-800 border border-gray-700 rounded-lg animate-pulse"
            ></div>
          ))}
        </div>
      </div>
    );
  }

  const COLORS = ['#8b5cf6', '#ec4899', '#06b6d4', '#10b981'];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white">Dashboard</h1>
        <p className="text-gray-400 mt-2">
          Welcome back! Here's your system overview.
        </p>
      </div>

      {/* Stats Grid */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            label="Total Devices"
            value={stats.totalDevices}
            icon={Smartphone}
            trend={12}
          />
          <StatCard
            label="Active Devices (24h)"
            value={stats.activeDevices24h}
            icon={Zap}
            trend={8}
          />
          <StatCard
            label="Total Channels"
            value={stats.totalChannels}
            icon={Tv}
            trend={3}
          />
          <StatCard
            label="API Requests Today"
            value={stats.apiRequestsToday}
            icon={Users}
            trend={-5}
          />
        </div>
      )}



      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Growth Chart */}
        <Card>
          <h3 className="text-lg font-bold text-white mb-4">User Growth (30 days)</h3>
          {userGrowth.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={userGrowth}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="_id" stroke="#9ca3af" />
                <YAxis stroke="#9ca3af" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1f2937',
                    border: '1px solid #374151',
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="count"
                  stroke="#8b5cf6"
                  dot={{ fill: '#8b5cf6' }}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-gray-400">No data available</p>
          )}
        </Card>

        {/* Top Channels */}
        <Card>
          <h3 className="text-lg font-bold text-white mb-4">Top Channels by Views</h3>
          {channelViews.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={channelViews}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="name" stroke="#9ca3af" />
                <YAxis stroke="#9ca3af" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1f2937',
                    border: '1px solid #374151',
                  }}
                />
                <Bar dataKey="views" fill="#8b5cf6" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-gray-400">No data available</p>
          )}
        </Card>
      </div>

      {/* Additional Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card>
            <h3 className="text-lg font-bold text-white mb-4">System Health</h3>
            <div className="space-y-3">
              <div>
                <p className="text-gray-400 text-sm">API Response Time</p>
                <p className="text-2xl font-bold text-green-400">145ms</p>
              </div>
              <div>
                <p className="text-gray-400 text-sm">Error Rate</p>
                <p className="text-2xl font-bold text-green-400">{stats.errorRate}</p>
              </div>
            </div>
          </Card>

          <Card>
            <h3 className="text-lg font-bold text-white mb-4">Content Stats</h3>
            <div className="space-y-3">
              <div>
                <p className="text-gray-400 text-sm">Total Views</p>
                <p className="text-2xl font-bold text-purple-400">
                  {stats.totalViews.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-gray-400 text-sm">Active Channels</p>
                <p className="text-2xl font-bold text-purple-400">
                  {stats.activeChannels}
                </p>
              </div>
            </div>
          </Card>

          <Card>
            <h3 className="text-lg font-bold text-white mb-4">Device Activity</h3>
            <div className="space-y-3">
              <div>
                <p className="text-gray-400 text-sm">Active (7 days)</p>
                <p className="text-2xl font-bold text-cyan-400">
                  {stats.activeDevices7d}
                </p>
              </div>
              <div>
                <p className="text-gray-400 text-sm">Total Registered</p>
                <p className="text-2xl font-bold text-cyan-400">
                  {stats.totalDevices}
                </p>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
